import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const ReadingComprehensionGame = ({ gameData, onComplete }) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [showFeedback, setShowFeedback] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [gamePhase, setGamePhase] = useState('reading'); // reading, quiz, complete

    const currentQuestion = gameData?.questions?.[currentQuestionIndex];

    const handleOptionSelect = (option) => {
        if (selectedOption) return; 
        setSelectedOption(option);
        triggerHaptic(20);

        if (option === currentQuestion.correct_answer) {
            setShowFeedback('correct');
            const pointsToAdd = gameData.points_per_question || 20;
            const newScore = score + pointsToAdd;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                setShowFeedback(null);
                setSelectedOption(null);

                if (currentQuestionIndex < gameData.questions.length - 1) {
                    setCurrentQuestionIndex(currentQuestionIndex + 1);
                } else {
                    setGamePhase('complete');
                    triggerHaptic([200, 100, 200, 100, 300]);
                    setTimeout(() => {
                        onComplete?.(newScore);
                    }, 4000);
                }
            }, 3000);
        } else {
            setShowFeedback('incorrect');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => {
                setShowFeedback(null);
                setSelectedOption(null);
            }, 2000);
        }
    };

    if (!gameData || !gameData.story || !gameData.questions) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-amber-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">📖</div>
                <h3 className="text-base md:text-3xl font-black text-amber-900 tracking-tighter uppercase">Vartomi puslapiai...</h3>
            </div>
        );
    }

    const progress = (currentQuestionIndex / gameData.questions.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-amber-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-amber-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        📖
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Skaitymo Nuotykis
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Žinios</p>
                        <p className="text-sm md:text-2xl font-black text-amber-600 leading-none">⭐ {score}</p>
                    </div>
                    {gamePhase === 'quiz' && (
                        <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                            <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Mįslė</p>
                            <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentQuestionIndex + 1}/{gameData.questions.length}</p>
                        </div>
                    )}
                </div>
            </div>

            {gamePhase === 'quiz' && (
                <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-amber-400 via-orange-500 to-amber-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(245,158,11,0.3)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            )}
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            {gamePhase === 'reading' ? (
                <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 shadow-2xl border-white/60 bg-white/60 backdrop-blur-xl relative overflow-hidden flex flex-col items-center">
                    <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[150px] opacity-[0.03] select-none pointer-events-none rotate-12 text-amber-900">📚</div>
                    <div className="w-full bg-white rounded-[2rem] md:rounded-[3.5rem] p-6 md:p-12 shadow-xl border-2 md:border-4 border-amber-50 relative group">
                        <div className="space-y-4 md:space-y-8 relative z-10">
                            {gameData.story.split('\n').map((paragraph, i) => (
                                <p key={i} className="text-base md:text-3xl font-bold text-slate-800 leading-relaxed italic font-serif text-center">
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl flex flex-col items-center relative overflow-hidden min-h-[200px]">
                    <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-8xl opacity-[0.03] select-none pointer-events-none text-amber-900">❓</div>
                    <div className="w-full max-w-4xl space-y-6 relative z-10 text-center">
                        <h4 className="text-[10px] font-black text-amber-500 uppercase tracking-[0.4em] mb-2">Klausimas</h4>
                        <h3 className="text-xl md:text-5xl font-black text-slate-900 tracking-tighter leading-tight drop-shadow-sm">
                            {currentQuestion.question}
                        </h3>
                    </div>
                </div>
            )}
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            {gamePhase === 'reading' ? (
                <button
                    onClick={() => { triggerHaptic(50); setGamePhase('quiz'); }}
                    className="premium-button-3d w-full py-8 md:py-12 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 text-white rounded-[2rem] md:rounded-[3.5rem] font-black text-lg md:text-4xl shadow-2xl flex items-center justify-center gap-4 group border-b-8 md:border-b-[12px] border-amber-700 active:translate-y-2 active:border-b-0 transition-all uppercase tracking-widest"
                >
                    <span>PERSKAIČIAU!</span>
                    <span className="group-hover:translate-x-2 transition-transform duration-500 text-2xl md:text-5xl">📖</span>
                </button>
            ) : (
                <div className="grid grid-cols-1 gap-3 md:gap-6 w-full max-w-4xl mx-auto">
                    {currentQuestion.options.map((option, index) => {
                        const isSelected = selectedOption === option;
                        const isCorrect = option === currentQuestion.correct_answer;
                        
                        let btnStyle = 'bg-white text-slate-700 border-slate-100 hover:scale-[1.02] active:scale-95';
                        if (isSelected && showFeedback === 'correct') btnStyle = 'bg-emerald-500 text-white border-emerald-700 scale-105 shadow-xl';
                        else if (isSelected && showFeedback === 'incorrect') btnStyle = 'bg-rose-500 text-white border-rose-700 animate-shake';
                        else if (showFeedback && isCorrect) btnStyle = 'bg-emerald-500/50 text-white border-emerald-400';

                        return (
                            <button
                                key={index}
                                onClick={() => handleOptionSelect(option)}
                                disabled={selectedOption !== null}
                                className={`
                                    relative flex items-center gap-4 p-4 md:p-8 rounded-2xl md:rounded-[3.5rem] text-sm md:text-3xl font-black transition-all duration-300 text-left border-b-4 md:border-b-8 shadow-lg
                                    ${btnStyle}
                                    disabled:cursor-default
                                `}
                            >
                                <div className={`
                                    w-10 h-10 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center text-sm md:text-2xl shrink-0 border-2 md:border-4 transition-all
                                    ${isSelected || (showFeedback && isCorrect) ? 'bg-white/20 border-white/40 text-white' : 'bg-slate-50 border-slate-200 text-slate-400'}
                                `}>
                                    {String.fromCharCode(65 + index)}
                                </div>
                                <span className="flex-1 drop-shadow-sm">{option}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );

    if (gamePhase === 'complete') {
        return (
            <div className="min-h-screen bg-amber-50 flex items-center justify-center p-4">
                <div className="premium-glass rounded-3xl md:rounded-[5rem] p-8 md:p-24 text-center border-white shadow-2xl animate-pop-in bg-white relative overflow-hidden flex flex-col items-center max-w-4xl w-full">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(245,158,11,0.05)_0%,transparent_70%)] pointer-events-none" />
                    <div className="text-[120px] md:text-[200px] mb-8 md:mb-12 drop-shadow-2xl animate-float">🏆📚</div>
                    <div className="space-y-6">
                        <h3 className="text-4xl md:text-7xl font-black text-slate-900 tracking-tighter uppercase leading-none">Tikras Žinovas!</h3>
                        <p className="text-lg md:text-3xl text-slate-500 font-bold italic font-serif leading-relaxed px-6">
                            "Tu sėkmingai perskaitei istoriją ir įveikei visus klausimus."
                        </p>
                    </div>
                    <div className="mt-12 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-10 md:px-20 py-4 md:py-8 rounded-[3rem] text-2xl md:text-5xl font-black shadow-xl border-b-8 border-amber-800">
                        ⭐ {score} TAŠKŲ
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {showFeedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[3rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${showFeedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl shadow-2xl border-4 border-inherit animate-bounce">
                            {showFeedback === 'correct' ? '📖' : '🕵️‍♂️'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter ${showFeedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {showFeedback === 'correct' ? 'TIKSLU!' : 'KLAIDA!'}
                            </h2>
                            <div className={`p-4 md:p-8 rounded-2xl border-2 italic font-serif leading-relaxed ${showFeedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-xl font-bold text-slate-700 text-center">
                                    {showFeedback === 'correct' ? 'Puikiai supratai istoriją!' : 'Pabandyk prisiminti detales!'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ReadingComprehensionGame;
